import { CommonModule, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localePL from '@angular/common/locales/pl';
import localeEN from '@angular/common/locales/en-US-POSIX';
import localeGR from '@angular/common/locales/el';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgxsModule, NoopNgxsExecutionStrategy } from '@ngxs/store';
import { StateClass } from '@ngxs/store/internals';
import { environment } from '../../../environments/environment';
import { AppRoutingModule } from '../../app-routing.module';
import { SharedModule } from '../shared/shared.module';
import { CustomDateAdapter } from './adapter/custom-date.adapter';
import { AddressFormComponent } from './components/address-form/address-form.component';
import { AlertComponent } from './components/alert/alert.component';
import { CessionAsideComponent } from './components/aside/cession-aside/cession-aside.component';
import { FrontierReadonlyComponent } from './components/aside/frontier-readonly/frontier-readonly.component';
import { GenericAsideComponent } from './components/aside/generic-aside/generic-aside.component';
import { MenuAsideComponent } from './components/aside/menu-aside/menu-aside.component';
import { MotorConfigAsideComponent } from './components/aside/motor-config-aside/motor-config-aside.component';
import { MotorReadonlyComponent } from './components/aside/motor-readonly/motor-readonly.component';
import { PartyEditAsideComponent } from './components/aside/party-edit-aside/party-edit-aside.component';
import { PropertyConfigAsideComponent } from './components/aside/property-config-aside/property-config-aside.component';
import { PropertyReadonlyComponent } from './components/aside/property-readonly/property-readonly.component';
import { ClientsBoxComponent } from './components/box/clients-box/clients-box.component';
import { ClientsSummaryBoxComponent } from './components/box/clients-summary-box/clients-summary-box.component';
import { MotorBoxComponent } from './components/box/motor-box/motor-box.component';
import { MotorSummaryBoxComponent } from './components/box/motor-summary-box/motor-summary-box.component';
import { PropertyBoxComponent } from './components/box/property-box/property-box.component';
import { PropertySummaryBoxComponent } from './components/box/property-summary-box/property-summary-box.component';
import { CarManualFormComponent } from './components/car-manual-form/car-manual-form.component';
import { ClausesComponent } from './components/clauses/clauses.component';
import { ContactDataDialogComponent } from './components/dialog/contact-data-dialog/contact-data-dialog.component';
import { EmailDataDialogComponent } from './components/dialog/email-data-dialog/email-data-dialog.component';
import { LocationDialogComponent } from './components/dialog/location-dialog/location-dialog.component';
import { ScheduleEditorDialogComponent } from './components/dialog/schedule-editor-dialog/schedule-editor-dialog.component';
import { EmptyComponent } from './components/empty/empty.component';
import { EurotaxFormComponent } from './components/eurotax-form/eurotax-form.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { FrontierSummaryComponent } from './components/frontier-summary/frontier-summary.component';
import { HeaderComponent } from './components/header/header.component';
import { DescriptionComponent } from './components/ioffer/description/description.component';
import { IofferMainComponent } from './components/ioffer/ioffer-main.component';
import { IOfferComponent } from './components/ioffer/ioffer/ioffer.component';
import { IPolicyComponent } from './components/ioffer/ipolicy/ipolicy.component';
import { IstyleguideComponent } from './components/ioffer/istyleguide/istyleguide.component';
import { IVerificationComponent } from './components/ioffer/iverification/iverification.component';
import { ReturnComponent } from './components/ioffer/return/return.component';
import { LanguageComponent } from './components/language/language.component';
import { LoginComponent } from './components/login/login.component';
import { LogoProjectComponent } from './components/logo-project/logo-project.component';
import { LogoComponent } from './components/logo/logo.component';
import { MainComponent } from './components/main/main.component';
import { MessageBoxComponent } from './components/message-box/message-box.component';
import { EditFrontierComponent } from './components/new-frontier/edit-frontier.component';
import { NewFrontierComponent } from './components/new-frontier/new-frontier.component';
import { NewOfferComponent } from './components/new-offer/new-offer.component';
import { CalculationCardComponent } from './components/offer/calculation-card/calculation-card.component';
import { CalculationSummaryComponent } from './components/offer/calculation-summary/calculation-summary.component';
import { OfferCardComponent } from './components/offer/offer-card/offer-card.component';
import { OfferComponent } from './components/offer/offer.component';
import { PartyFormComponent } from './components/party-form/party-form.component';
import { PasswordResetComponent } from './components/password-reset/password-reset.component';
import { PolicyCardComponent } from './components/policy-card/policy-card.component';
import { RegistryComponent } from './components/registry/registry.component';
import { SalespointComponent } from './components/salespoint/salespoint.component';
import { StyleguideComponent } from './components/styleguide/styleguide.component';
import { TimetableComponent } from './components/timetable/timetable.component';
import { AsideContentDirective } from './directives/aside-content.directive';
import { DynamicAsideContentDirective } from './directives/dynamic-aside-content.directive';
import { DynamicAsideDirective } from './directives/dynamic-aside.directive';
import { BlockerInterceptor } from './interceptor/blocker.interceptor';
import { AccessDeniedInterceptor } from './interceptor/denied.interceptor';
import { ErrorInterceptor } from './interceptor/error.interceptor';
import { LanguageInterceptor } from './interceptor/language.interceptor';
import { TimeoutInterceptor } from './interceptor/timeout.interceptor';
import { UnauthorizedInterceptor } from './interceptor/unauthorized.interceptor';
import { UuidInterceptor } from './interceptor/uuid.interceptor';
import { AsmService } from './service/asm.service';
import { BlockerService } from './service/blocker.service';
import { DictionaryService } from './service/dictionary.service';
import { EurotaxService } from './service/eurotax.service';
import { FileService } from './service/file.service';
import { FrontierService } from './service/frontier.service';
import { GrowlService } from './service/growl.service';
import { HogaService } from './service/hoga.service';
import { IOfferService } from './service/ioffer.service';
import { LanguageService } from './service/language.service';
import { OfferService } from './service/offer.service';
import { PolicyService } from './service/policy.service';
import { ScheduleService } from './service/schedule.service';
import { SidenavService } from './service/sidenav.service';
import { UserService } from './service/user.service';
import { AsideState } from './store/aside.state';
import { DialogState } from './store/dialog.state';
import { DictionaryState } from './store/dictionary.state';
import { ErrorsState } from './store/errors.state';
import { FrontierState } from './store/frontier.state';
import { IOfferState } from './store/ioffer.state';
import { OfferState } from './store/offer.state';
import { PolicyState } from './store/policy.state';
import { RegistryState } from './store/registry.state';
import { RouterState } from './store/router.state';
import { ScheduleState } from './store/schedule.state';
import { UserState } from './store/user.state';

registerLocaleData(localePL);
registerLocaleData(localeGR);
registerLocaleData(localeEN);

@NgModule({
    declarations: [
        LoginComponent,
        StyleguideComponent,
        NewOfferComponent,
        LogoComponent,
        DynamicAsideContentDirective,
        DynamicAsideDirective,
        AsideContentDirective,
        MenuAsideComponent,
        GenericAsideComponent,
        AlertComponent,
        SalespointComponent,
        HeaderComponent,
        PartyFormComponent,
        AddressFormComponent,
        OfferComponent,
        CalculationCardComponent,
        RegistryComponent,
        ClientsBoxComponent,
        PropertyBoxComponent,
        MainComponent,
        MessageBoxComponent,
        MotorBoxComponent,
        PropertyConfigAsideComponent,
        MotorConfigAsideComponent,
        EurotaxFormComponent,
        CarManualFormComponent,
        PartyEditAsideComponent,
        CalculationSummaryComponent,
        ClausesComponent,
        ClientsSummaryBoxComponent,
        OfferCardComponent,
        PropertySummaryBoxComponent,
        MotorSummaryBoxComponent,
        LocationDialogComponent,
        ContactDataDialogComponent,
        CessionAsideComponent,
        PropertyReadonlyComponent,
        MotorReadonlyComponent,
        PolicyCardComponent,
        PasswordResetComponent,
        TimetableComponent,
        ForgotPasswordComponent,
        LogoProjectComponent,
        EmptyComponent,
        ScheduleEditorDialogComponent,
        IofferMainComponent,
        IOfferComponent,
        IVerificationComponent,
        IstyleguideComponent,
        IPolicyComponent,
        DescriptionComponent,
        EmailDataDialogComponent,
        ReturnComponent,
        NewFrontierComponent,
        EditFrontierComponent,
        FrontierSummaryComponent,
        FrontierReadonlyComponent,
        LanguageComponent,
    ],
    entryComponents: [
        PropertyConfigAsideComponent,
        MotorConfigAsideComponent,
        PartyEditAsideComponent,
        PropertyReadonlyComponent,
        MotorReadonlyComponent,
        FrontierReadonlyComponent,
    ],
    imports: [
        CommonModule,
        FormsModule,
        SharedModule,
        AppRoutingModule,
        TranslateModule.forRoot(),
        NgxsModule.forRoot([
            AsideState,
            ErrorsState,
            UserState,
            DialogState,
            RouterState,
            OfferState,
            DictionaryState,
            RegistryState,
            PolicyState,
            ScheduleState,
            IOfferState,
            FrontierState,
        ] as StateClass[], {
            executionStrategy: NoopNgxsExecutionStrategy,
            developmentMode: !environment.production
        }),
    ],
    exports: [
        FormsModule,
        AppRoutingModule,
        AsideContentDirective,
        DynamicAsideContentDirective,
        DynamicAsideDirective,
    ],
    providers: [
        GrowlService,
        LanguageService,
        SidenavService,
        UserService,
        DictionaryService,
        OfferService,
        HogaService,
        EurotaxService,
        BlockerService,
        FileService,
        PolicyService,
        ScheduleService,
        IOfferService,
        FrontierService,
        AsmService,
        {
            provide: LOCALE_ID,
            deps: [LanguageService],
            useFactory: (languageService) => languageService.getCurrent()
        },
        {provide: HTTP_INTERCEPTORS, useClass: UuidInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: AccessDeniedInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: BlockerInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: UnauthorizedInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true},
        {provide: DateAdapter, useClass: CustomDateAdapter},
    ]
})
export class CoreModule {
}
